'use client'

import PropTypes from 'prop-types'
import MUIBadge from '@mui/material/Badge'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIIconButton from '@mui/material/IconButton'
import MUITooltip from '@mui/material/Tooltip'
import MUIStack from '@mui/material/Stack'
import { FunnelIcon } from '@repo/ui/FunnelIcon'
import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import { useMenuState } from '@szhsin/react-menu'
import { PopupWithBackdrop } from '@repo/ui/PopupWithBackdrop'
import { useRef, useState } from 'react'

export const FilterMenu = ({ onFilterChange, options, defaultValues = {} }) => {
  const [menuState, toggleMenu] = useMenuState({ transition: true })
  const ref = useRef(null)
  const [selectedItems, setSelectedItems] = useState(defaultValues)

  const activeFilterCount = Object.values(selectedItems).filter((v) => v != null).length

  const handleSelect = (key, value) => {
    const newSelectedItems = { ...selectedItems, [key]: value }
    setSelectedItems(newSelectedItems)
    onFilterChange(key, value)
  }

  const handleBooleanToggle = (key) => {
    const currentValue = selectedItems[key]
    const newValue = currentValue ? null : true

    const newSelectedItems = { ...selectedItems, [key]: newValue }
    setSelectedItems(newSelectedItems)
    onFilterChange(key, newValue)
  }

  return (
    <>
      <MUITooltip title="Filters" arrow placement="top">
        <MUIIconButton data-testid="FilterMenu" onClick={() => toggleMenu(true)} ref={ref}>
          <MUIBadge
            badgeContent={activeFilterCount}
            color="primary"
            sx={{
              [`.MuiBadge-badge`]: { width: '1rem', height: '20px', fontSize: '0.75rem' }
            }}
            invisible={activeFilterCount === 0}
          >
            <FunnelIcon
              isSelected={activeFilterCount > 0}
              sx={{
                width: '22px',
                height: '22px',
                color: activeFilterCount > 0 ? '#5073C3' : 'rgba(61, 81, 123, 0.56)'
              }}
            />
          </MUIBadge>
        </MUIIconButton>
      </MUITooltip>

      <PopupWithBackdrop
        menuState={menuState}
        anchorRef={ref}
        toggleMenu={toggleMenu}
        arrow
        zIndex={1500}
        className="filterMenu"
      >
        <MUIStack direction="column" gap={2} sx={{ padding: '16px 20px', minWidth: '280px' }}>
          {options?.map(({ title, key, items, type }) => {
            if (type === 'boolean') {
              return (
                <MUIFormControlLabel
                  key={key}
                  control={<MUICheckbox checked={!!selectedItems[key]} onChange={() => handleBooleanToggle(key)} />}
                  label={title}
                />
              )
            }

            const selectedValue = selectedItems[key]
              ? items?.find((item) => item.value === selectedItems[key]) || null
              : null

            return (
              <ReactSelectMenu
                key={key}
                label={title}
                onChange={(newVal) => {
                  handleSelect(key, newVal?.value || null)
                }}
                labelKey="label"
                valueKey="value"
                items={items || []}
                value={selectedValue}
                searchable
                size="small"
                menuZindex={9999999}
              />
            )
          })}
        </MUIStack>
      </PopupWithBackdrop>
    </>
  )
}

FilterMenu.propTypes = {
  onFilterChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      key: PropTypes.string,
      type: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.any
        })
      )
    })
  ),
  defaultValues: PropTypes.object
}
